import { useMemo } from "react";

import { useSelector } from "react-redux";
import styled from "styled-components";

import { shortMonthAndYear } from "@similarweb/pro-logic-components";

import { websiteWorldWideOverviewLogic } from "app/pages/website-performance/helpers/websitePerformanceHelpers";
import { connectedAccountsServiceFactory } from "app/services/connectedAccounts.service";
import CountryService from "app/services/CountryService";
import DurationService from "app/services/DurationService";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { RootState } from "single-spa/store/types";

import { ConversionRateWidget } from "./ConversionRate/ConversionRateWidget";
import { useConversionRateAvailability } from "./ConversionRate/useConversionRateAvailability";
import { DeviceDistributionWidget } from "./DeviceDistribution/DeviceDistributionWidget";
import { MonthlyActiveUsersWidget } from "./MonthlyActiveUsers/MonthlyActiveUsersWidget";
import { TotalVisitsWidget } from "./TotalVisits/TotalVisitsWidget";
import { WebsiteRankWidget } from "./WebsiteRank/WebsiteRankWidget";

const WidgetsGrid = styled.div<{ $hasConversionRateWidget: boolean }>`
    display: grid;
    gap: 24px;
    margin-bottom: 24px;
    width: 100%;

    grid-template-columns: ${({ $hasConversionRateWidget }) =>
        $hasConversionRateWidget ? "repeat(2, 1fr)" : "repeat(3, 1fr)"};
    grid-template-rows: ${({ $hasConversionRateWidget }) => ($hasConversionRateWidget ? "214px 250px" : "250px")};

    & > * {
        min-width: 0;
    }
`;

const TotalVisitsComponentsContainer = () => {
    const showGAApprovedData = useSelector((state: RootState) => state.common.showGAApprovedData);
    const swConnectedAccountsService = connectedAccountsServiceFactory();
    const params = proNavigator.getParams();
    const { apiController, isWWWithoutPermissions } = websiteWorldWideOverviewLogic(params.country);
    if (isWWWithoutPermissions) {
        params.duration = "3m";
    }
    const { duration, comparedDuration, webSource, country, key } = params;

    const durationObject = DurationService.getDurationData(duration, comparedDuration);
    const dateRangeText = `${durationObject.raw.from.format(shortMonthAndYear)} - ${durationObject.raw.to.format(shortMonthAndYear)}`;
    const countryText = CountryService.getCountryById(country)?.text ?? "";

    const apiParams = useMemo(() => {
        const responseParams = proNavigator.getApiParams(params);
        const { compareFrom, compareTo } = durationObject?.forAPI;
        return {
            ...responseParams,
            compareFrom,
            compareTo,
            duration,
        };
    }, [params, durationObject]);

    const isGAPrivate = swConnectedAccountsService.isDomainInGoogleAnalyticsAccountPrivateSites(apiParams.keys ?? key);
    const conversionRateAvailability = useConversionRateAvailability({
        keys: [apiParams.key],
        from: apiParams.from,
        to: apiParams.to,
        isWWW: apiParams.isWWW,
    });
    const hasConversionRateWidget = conversionRateAvailability.state !== "hidden";

    return (
        <WidgetsGrid $hasConversionRateWidget={hasConversionRateWidget}>
            <TotalVisitsWidget
                params={apiParams}
                dateRangeText={dateRangeText}
                countryText={countryText}
                countryCode={country}
                showGAApprovedData={showGAApprovedData}
                isGAPrivate={isGAPrivate}
                apiController={apiController}
            />
            <ConversionRateWidget
                params={apiParams}
                dateRangeText={dateRangeText}
                countryText={countryText}
                countryCode={country}
                availability={conversionRateAvailability}
            />
            {webSource === devicesTypes.TOTAL ? (
                <DeviceDistributionWidget
                    params={apiParams}
                    dateRangeText={dateRangeText}
                    countryText={countryText}
                    countryCode={country}
                    showGAApprovedData={showGAApprovedData}
                    isGAPrivate={isGAPrivate}
                    apiController={apiController}
                />
            ) : (
                <MonthlyActiveUsersWidget
                    params={apiParams}
                    showGAApprovedData={showGAApprovedData}
                    isGAPrivate={isGAPrivate}
                />
            )}
            <WebsiteRankWidget
                params={apiParams}
                apiController={apiController}
            />
        </WidgetsGrid>
    );
};

export default TotalVisitsComponentsContainer;
